// File 1: /src/components/Charts/utils/analyticsPie.js

/**
 * Normalize raw API rows into [{ name, value }] and aggregate duplicates by name.
 *
 * The function looks for the first existing field from nameFieldKeys to use as the display name,
 * and the first existing field from valueFieldKeys to use as the numeric value.
 *
 * @param {Array<Object>} rowsList - Raw rows returned from API
 * @param {Array<string>} nameFieldKeys - Ordered list of candidate fields that may hold the name (e.g., ['drug_name', 'name'])
 * @param {Array<string>} valueFieldKeys - Ordered list of candidate fields that may hold the numeric value (e.g., ['drug_count', 'count'])
 * @returns {Array<{name: string, value: number}>} Aggregated rows with unique names and summed values
 */
export function normalizeRows(rowsList, nameFieldKeys = [], valueFieldKeys = []) {
  const aggregatedValuesByName = new Map();

  (rowsList || []).forEach((row) => {
    // Find which key actually exists for name and value
    const nameFieldKey = nameFieldKeys.find((fieldKey) => row?.[fieldKey] != null);
    const valueFieldKey = valueFieldKeys.find((fieldKey) => row?.[fieldKey] != null);

    const displayName = nameFieldKey ? row[nameFieldKey] : null;
    const rawValue = valueFieldKey ? row[valueFieldKey] : 0;
    const numericValue = Number(rawValue) || 0;

    // Skip invalid or non-positive entries
    if (!displayName || numericValue <= 0) return;

    // Aggregate duplicates by name
    const previousValue = aggregatedValuesByName.get(displayName) || 0;
    aggregatedValuesByName.set(displayName, previousValue + numericValue);
  });

  // Convert Map back to array format
  return Array.from(aggregatedValuesByName, ([displayName, numericValue]) => ({
    name: displayName,
    value: numericValue,
  }));
}

/**
 * Compute the sum of all values in normalized rows.
 *
 * @param {Array<{name: string, value: number}>} rowsList
 * @returns {number}
 */
export function sumTotal(rowsList) {
  return (rowsList || []).reduce((accumulator, currentRow) => {
    return accumulator + (Number(currentRow.value) || 0);
  }, 0);
}

/**
 * Keep the top N rows and aggregate the remainder into a single "Others" row.
 *
 * @param {Array<{name: string, value: number}>} rowsList - Normalized rows
 * @param {number} topN - Number of top rows to keep
 * @param {string} othersLabel - Label to use for the aggregated remainder
 * @param {number} othersExtra - Optional extra amount to add into "Others" (legacy API sums, etc.)
 * @returns {Array<{name: string, value: number}>}
 */
export function aggregateTopN(
  rowsList,
  topN = 4,
  othersLabel = 'Others',
  othersExtra = 0,
  coverageRatio = null
) {
  const coverageProvided = typeof coverageRatio === 'number' && !Number.isNaN(coverageRatio);
  const normalizedRows = Array.isArray(rowsList) ? [...rowsList] : [];

  if (normalizedRows.length === 0) {
    return othersExtra > 0 ? [{ name: othersLabel, value: othersExtra }] : [];
  }

  if ((!topN || topN <= 0) && !coverageProvided) {
    return [...normalizedRows];
  }

  const sortedByValueDesc = [...normalizedRows].sort((a, b) => b.value - a.value);
  const normalizedCoverage = coverageProvided
    ? Math.min(
        Math.max(coverageRatio > 1 ? coverageRatio / 100 : coverageRatio, 0),
        1
      )
    : null;

  const total = sumTotal(sortedByValueDesc) + (othersExtra || 0);
  const hasTopLimit = typeof topN === 'number' && topN > 0;
  const maxSlices = hasTopLimit
    ? Math.min(topN, sortedByValueDesc.length)
    : sortedByValueDesc.length;

  let effectiveTopCount;

  if (normalizedCoverage != null && total > 0) {
    let cumulative = 0;
    effectiveTopCount = 0;

    while (effectiveTopCount < maxSlices) {
      cumulative += sortedByValueDesc[effectiveTopCount].value;
      effectiveTopCount += 1;

      if (cumulative / total >= normalizedCoverage) {
        break;
      }
    }

    if (effectiveTopCount === 0) {
      effectiveTopCount = Math.min(1, sortedByValueDesc.length);
    }
  } else if (hasTopLimit) {
    effectiveTopCount = maxSlices;
  } else {
    effectiveTopCount = sortedByValueDesc.length;
  }

  const topItems = sortedByValueDesc.slice(0, effectiveTopCount);
  const remainingItems = sortedByValueDesc.slice(effectiveTopCount);

  if (remainingItems.length === 0 && !othersExtra) {
    return topItems;
  }

  const othersTotal = sumTotal(remainingItems) + (othersExtra || 0);

  return othersTotal > 0
    ? [...topItems, { name: othersLabel, value: othersTotal }]
    : topItems;
}

/**
 * Convert normalized rows into ECharts pie series data, assigning colors.
 *
 * Color resolution order:
 * 1) colorByNameMap[name] if provided
 * 2) othersColor if the row is the aggregated "Others"
 * 3) baseColors[index % baseColors.length] as a fallback palette
 *
 * @param {Array<{name: string, value: number}>} rowsList - Rows to display in the chart (already aggregated as needed)
 * @param {Array<string>} baseColors - Palette of hex colors (e.g., ['#0A5DBB', '#1B5E20', ...])
 * @param {string} othersColor - Color to use for the "Others" slice
 * @param {string} [othersLabel='Others'] - The label used for the aggregated "Others" row
 * @param {Object<string,string>|null} [colorByNameMap=null] - Optional map from name -> color
 * @returns {Array<{value: number, name: string, itemStyle: { color: string }}>}
 */
export function toEchartData(
  rowsList,
  baseColors,
  othersColor,
  othersLabel = 'Others',
  colorByNameMap = null
) {
  return rowsList.map((rowItem, index) => {
    const isOthersSlice = rowItem.name === othersLabel;

    const resolvedColor =
      (colorByNameMap && colorByNameMap[rowItem.name]) ||
      (isOthersSlice ? othersColor : baseColors[index % baseColors.length]);

    return {
      value: rowItem.value,
      name: rowItem.name,
      itemStyle: { color: resolvedColor },
    };
  });
}